import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Swym's document mousedown listener opens the BIS form on every click when #BIS_trigger
// exists in the DOM. This capture-phase guard (capture fires before bubble, regardless of
// registration order) blocks Swym's bubble listener unless the click was on the BIS button.
document.addEventListener(
  'mousedown',
  (e) => {
    if (
      document.getElementById('BIS_trigger') &&
      !(/** @type {Element} */ (e.target)).closest('#BIS_trigger')
    ) {
      e.stopImmediatePropagation();
    }
  },
  true
);

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// Desktop: cursor-following tooltip for swatch-limit buttons.
let lastTouchTime = 0;
document.addEventListener('touchstart', () => { lastTouchTime = Date.now(); }, { capture: true, passive: true });

/** @type {HTMLElement | null} */
let swatchLimitTooltip = null;
/** @type {number} */
let swatchLimitTooltipWidth = 0;
/** @type {number | null} */
let swatchTooltipRafId = null;
/** @type {HTMLElement | null} */
let swatchTooltipPendingEl = null;
let swatchTooltipPendingX = 0;
let swatchTooltipPendingY = 0;

function getSwatchLimitTooltip() {
  if (!swatchLimitTooltip) {
    swatchLimitTooltip = document.createElement('div');
    swatchLimitTooltip.className = 'swatch-limit-tooltip';
    swatchLimitTooltip.setAttribute('aria-hidden', 'true');
    document.body.appendChild(swatchLimitTooltip);
  }
  return swatchLimitTooltip;
}

document.addEventListener('mousemove', (e) => {
  // Ignore synthetic mouse events generated ~300 ms after a touch interaction
  if (Date.now() - lastTouchTime < 600) return;
  if (!(e.target instanceof Element)) return;

  swatchTooltipPendingEl = /** @type {HTMLElement | null} */ (e.target.closest('[data-swatch-limit]'));
  swatchTooltipPendingX = e.clientX;
  swatchTooltipPendingY = e.clientY;

  if (swatchTooltipRafId !== null) return; // frame already scheduled; latest coords are stored above

  swatchTooltipRafId = requestAnimationFrame(() => {
    swatchTooltipRafId = null;
    const tooltip = getSwatchLimitTooltip();

    if (swatchTooltipPendingEl) {
      tooltip.textContent = swatchTooltipPendingEl.getAttribute('data-swatch-limit') || '';
      tooltip.style.display = 'block';

      // Read offsetWidth once per hover to avoid forced reflow on every frame
      if (!swatchLimitTooltipWidth) swatchLimitTooltipWidth = tooltip.offsetWidth;
      const gap = 14;
      const tooltipWidth = swatchLimitTooltipWidth;
      const wouldOverflowRight = swatchTooltipPendingX + gap + tooltipWidth > window.innerWidth;

      tooltip.style.left = wouldOverflowRight
        ? `${swatchTooltipPendingX - gap - tooltipWidth}px`
        : `${swatchTooltipPendingX + gap}px`;
      tooltip.style.top = `${swatchTooltipPendingY}px`;
      tooltip.style.transform = 'translateY(-50%)';
    } else {
      swatchLimitTooltipWidth = 0;
      tooltip.style.display = 'none';
    }
  });
});

// Mobile: icon swap, shake animation, and a tooltip anchored below the button
/** @type {ReturnType<typeof setTimeout> | null} */
let swatchLimitMobileTimer = null;
/** @type {HTMLElement | null} */
let swatchLimitMobileTooltip = null;

function getSwatchLimitMobileTooltip() {
  if (!swatchLimitMobileTooltip) {
    swatchLimitMobileTooltip = document.createElement('div');
    swatchLimitMobileTooltip.className = 'swatch-limit-tooltip swatch-limit-tooltip--mobile';
    swatchLimitMobileTooltip.setAttribute('aria-hidden', 'true');
    document.body.appendChild(swatchLimitMobileTooltip);
  }
  return swatchLimitMobileTooltip;
}

document.addEventListener('pointerup', (e) => {
  if (e.pointerType === 'mouse') return;
  if (!(e.target instanceof Element)) return;
  const limitEl = /** @type {HTMLElement | null} */ (e.target.closest('[data-swatch-limit]'));
  if (!limitEl) return;

  const message = limitEl.getAttribute('data-swatch-limit') || '';
  const btn = /** @type {HTMLElement | null} */ (limitEl.querySelector('[data-add-to-cart-button]'));
  if (!btn) return;

  // Swap icon to close (X)
  const icon = /** @type {HTMLElement | null} */ (btn.querySelector('.add-to-cart-icon'));
  if (icon && !icon.dataset.originalHtml) {
    icon.dataset.originalHtml = icon.innerHTML;
    icon.classList.add('add-to-cart-icon--limit');
    icon.innerHTML = `<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }

  // Shake animation
  btn.classList.remove('swatch-limit-shake');
  void btn.offsetWidth;
  btn.classList.add('swatch-limit-shake');

  // Position tooltip below the button using fixed coordinates.
  const mobileTooltip = getSwatchLimitMobileTooltip();
  const rect = btn.getBoundingClientRect();
  mobileTooltip.textContent = message;
  mobileTooltip.style.left = `${rect.right}px`;
  mobileTooltip.style.top = `${rect.bottom + 6}px`;
  mobileTooltip.classList.remove('swatch-limit-tooltip--hiding');
  mobileTooltip.style.display = 'block';

  const FADE_DURATION = 200;
  const hideAndRestore = () => {
    // Fade out first, then snap to display:none after the animation completes
    mobileTooltip.classList.add('swatch-limit-tooltip--hiding');
    setTimeout(() => {
      mobileTooltip.style.display = 'none';
      mobileTooltip.classList.remove('swatch-limit-tooltip--hiding');
    }, FADE_DURATION);
    btn.classList.remove('swatch-limit-shake');
    if (icon && icon.dataset.originalHtml) {
      icon.innerHTML = icon.dataset.originalHtml;
      icon.classList.remove('add-to-cart-icon--limit');
      delete icon.dataset.originalHtml;
    }
    swatchLimitMobileTimer = null;
  };

  // Hide immediately on scroll so tooltip doesn't appear detached from button
  window.addEventListener('scroll', hideAndRestore, { passive: true, capture: true, once: true });

  // Always restart the timer on each tap
  if (swatchLimitMobileTimer) clearTimeout(swatchLimitMobileTimer);
  swatchLimitMobileTimer = setTimeout(() => {
    window.removeEventListener('scroll', hideAndRestore, { capture: true });
    hideAndRestore();
  }, 3500);
});

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  /** @type {boolean} */
  #swatchPersisted = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);

    if (this.refs.addToCartButton?.classList.contains('swatch-faded')) {
      this.#swatchPersisted = true;
    }
  }

  get isPersisted() {
    return this.#swatchPersisted;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        // Don't clean up if a swatch persist is active
        if (this.#swatchPersisted) return;
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * For swatch products: keeps the "Added" state visible, fades it after the animation completes, and sets stay-visible on the parent quick-add element.
   */
  persistAddedState() {
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.#swatchPersisted = true;

    const quickAdd = this.closest('quick-add-component');
    quickAdd?.toggleAttribute('stay-visible', true);

    this.#animationTimeout = setTimeout(() => {
      this.refs.addToCartButton.classList.add('swatch-faded');
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Resets the button back to its normal add state (used when swatch is removed from cart).
   */
  resetAddedState() {
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.#swatchPersisted = false;

    const quickAdd = this.closest('quick-add-component');
    quickAdd?.toggleAttribute('stay-visible', false);

    const btn = this.refs.addToCartButton;
    btn.classList.remove('atc-added', 'swatch-faded');
    this.enable();
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Syncs cart quantity and (for swatches) limit/disabled state on cart updates.
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    if (event.detail?.sourceId === this.id) return;

    await this.#fetchAndUpdateCartQuantity();

    const isSwatch = Boolean(this.querySelector('[data-swatch-property][value="true"]'));
    if (!isSwatch) return;

    const container = this.refs.addToCartButtonContainer;
    if (!container) return;

    const cartSwatches = await this.checkForSwatches();

    if (container.isPersisted) {
      if (!cartSwatches.hasMatchingSwatchInCart) {
        container.resetAddedState();
      }
      return;
    }

    if (cartSwatches.hasMatchingSwatchInCart) {
      container.disable();
      return;
    }

    if (cartSwatches.swatchCount >= 6) {
      container.setAttribute('data-swatch-limit', 'Your swatch limit has been reached');
      container.disable();
    } else {
      container.removeAttribute('data-swatch-limit');
      container.enable();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) this.setErrorMessage(errorMessage);

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const isSwatchToAdd = Boolean(this.querySelector('[data-swatch-property][value="true"]'));

    if (isSwatchToAdd) {
      // Disable synchronously before the async cart check so a second click
      // during the network round-trip can't slip through and double-add.
      addToCartButtonContainer?.disable();

      const cartSwatches = await this.checkForSwatches();

      if (cartSwatches.swatchCount >= 6) {
        addToCartButtonContainer?.enable();
        return;
      }

      if (cartSwatches.hasMatchingSwatchInCart) {
        addToCartButtonContainer?.enable();
        return;
      }

      addToCartButtonContainer?.persistAddedState();
    }

    const formData = new FormData(form);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }

      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('items[0][id]') || '', response.message, response.description, response.errors)
          );

          // Undo swatch persist state since the add failed
          if (isSwatchToAdd) {
            addToCartButtonContainer?.resetAddedState();
          }

          if (!addToCartTextError) return;
          this.setErrorMessage(response.message);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('items[0][id]');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
        if (isSwatchToAdd) {
          addToCartButtonContainer?.resetAddedState();
        }
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  setErrorMessage(message) {
    const { addToCartTextError } = this.refs;

    if (!addToCartTextError) return;

    addToCartTextError.classList.remove('hidden');

    // Reuse the text node if the user is spam-clicking
    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = message;
    } else {
      const newTextNode = document.createTextNode(message);
      addToCartTextError.appendChild(newTextNode);
    }

    // Create or get existing error live region for screen readers
    this.#setLiveRegionText(message);

    this.#timeout = setTimeout(() => {
      if (!addToCartTextError) return;
      addToCartTextError.classList.add('hidden');

      // Clear the announcement
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  async checkForSwatches() {
    // Bypass any HTTP cache so the duplicate / limit checks always see the latest cart state
    const response = await fetch('/cart.js', { cache: 'no-store' });
    const cart = await response.json();
    const cartItems = cart.items;
    const variantId = String(this.querySelector("[data-variant-id]")?.value || '');
    const swatchToAdd = this.querySelector("[data-swatch-property]");

    let hasMatchingSwatchInCart = false;

    if (!cartItems) {
      return { swatchCount: 0, hasMatchingSwatchInCart: false };
    }

    const swatchCount = cartItems.reduce((count, item) => {
      if (swatchToAdd && String(item.variant_id) === variantId) hasMatchingSwatchInCart = true;

      return count + (item.properties?._swatch ? 1 : 0);
    }, 0);

    return { swatchCount, hasMatchingSwatchInCart };
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (currentAddToCartButton) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
        // morph() uses childrenOnly which skips root-element attributes; sync them from server HTML
        const newButton = /** @type {HTMLButtonElement} */ (newAddToCartButton);
        currentAddToCartButton.disabled = newButton.disabled;
        if (currentAddToCartButton.id !== newButton.id) {
          currentAddToCartButton.id = newButton.id;
        }
        if (currentAddToCartButton.type !== newButton.type) {
          currentAddToCartButton.type = newButton.type;
        }
        const newOnClick = newButton.getAttribute('on:click');
        if (currentAddToCartButton.getAttribute('on:click') !== newOnClick) {
          if (newOnClick !== null) {
            currentAddToCartButton.setAttribute('on:click', newOnClick);
          } else {
            currentAddToCartButton.removeAttribute('on:click');
          }
        }
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
